import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import MessageEventModal from "go-component/components/MessageEvent/MessageEventModal";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import FormatResourceStatus from "../../../../../../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	PaymentMethodApi,
	PaymentMethodTPayPaymentsApi,
} from "../../../../../../../../../../services/Api/Organization/types";
import PaymentMethodForm from "../../components/PaymentMethodForm";
import { getPaymentMethodClientSecret, getPaymentMethodSecretClientId } from "../../utils/onlinePayments";

interface MatchParams {
	paymentMethodId: string;
	organizationId: string;
}

const OrganizationSettingsPaymentMethodsEditPage = (): JSX.Element => {
	const [paymentMethod, setPaymentMethod] = useState<PaymentMethodApi | undefined>();
	const [showLogs, setShowLogs] = useState(false);
	const organization = useSelector(selectOrganization);
	const history = useHistory();
	const confirmation = useConfirmation();
	const { addFlash, addSuccessFlash } = useFlash();
	const { t } = useTranslation();
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const isMobile = useWindowSize().isMobile;
	const { paymentMethodId } = useParams<MatchParams>();
	const [tPayPayments, setTPayPayments] = useState<PaymentMethodTPayPaymentsApi | undefined>();

	useEffect(() => {
		onFetchPaymentMethod();
	}, []);

	useEffect(() => {
		if (!paymentMethod) return;

		const secretClientId = getPaymentMethodSecretClientId(paymentMethod.settings || []);
		const clientSecret = getPaymentMethodClientSecret(paymentMethod.settings || []);

		if (secretClientId && clientSecret) {
			fetchTPayPayments();
		} else {
			setTPayPayments(undefined);
		}
	}, [paymentMethod?.id]);

	const onFetchPaymentMethod = async () => {
		try {
			const params: Record<string, string> = { include: "custom_field_templates,roles,settings" };
			const res = await apiOrganization.getPaymentMethod(Number(paymentMethodId), params);
			handleChangeTabTitle(`${res.name} | ${t("common.word.payment_methods")}`);
			setPaymentMethod(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchTPayPayments = async () => {
		try {
			const res = await apiOrganization.getTPayPayments();
			setTPayPayments(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const removePaymentMethod = async (paymentMethodToBeRemoved: PaymentMethodApi) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.remove", { ns: "lib" }),
			});
			await apiOrganization.removePaymentMethod(Number(paymentMethodToBeRemoved.id));
			history.push(`/${organization.id}/settings/payment_methods`);
			addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const restorePaymentMethod = async (paymentMethodToBeRestored: PaymentMethodApi) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.activate", { ns: "lib" }),
			});
			const res = await apiOrganization.restorePaymentMethod(Number(paymentMethodToBeRestored.id));
			setPaymentMethod(res);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const disablePaymentMethod = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.disable", { ns: "lib" }),
			});
			const res = await apiOrganization.disablePaymentMethod(Number(paymentMethod?.id));
			setPaymentMethod(res);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!paymentMethod) {
		return <LoadingContainer />;
	}

	const renderTitle = () => (
		<>
			{t("common.word.payment_methods")} "{paymentMethod.name}"
			<FormatResourceStatus status={paymentMethod.status} />
		</>
	);

	const buttons: ButtonProps[] = [
		{
			action: () => setShowLogs(!showLogs),
			title: t("common.word.logs", { ns: "lib" }),
			variant: "light",
		},
		{
			title: t("common.action.remove", { ns: "lib" }),
			dropdown: true,
			hide: !(!paymentMethod.default && paymentMethod.status !== "DELETED"),
			action: () => removePaymentMethod(paymentMethod),
		},
		{
			action: () => restorePaymentMethod(paymentMethod),
			title: t("common.action.activate", { ns: "lib" }),
			dropdown: true,
			hide: !(
				(!paymentMethod.default && paymentMethod.status === "DELETED") ||
				paymentMethod.status === "DISABLED"
			),
		},
		{
			action: () => disablePaymentMethod(),
			title: t("common.action.disable", { ns: "lib" }),
			dropdown: true,
			hide: paymentMethod.status !== "ENABLED",
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			action: () => setShowLogs(!showLogs),
			title: t("common.word.logs", { ns: "lib" }),
		},
		{
			title: t("common.action.remove", { ns: "lib" }),
			hide: !(!paymentMethod.default && paymentMethod.status !== "DELETED"),
			action: () => removePaymentMethod(paymentMethod),
		},
		{
			action: () => restorePaymentMethod(paymentMethod),
			title: t("common.action.activate", { ns: "lib" }),
			hide: !(
				(!paymentMethod.default && paymentMethod.status === "DELETED") ||
				paymentMethod.status === "DISABLED"
			),
		},
		{
			action: () => disablePaymentMethod(),
			title: t("common.action.disable", { ns: "lib" }),
			hide: paymentMethod.status !== "ENABLED",
		},
	];

	return (
		<>
			<MobileActions actions={mobileActions} />
			<Header title={renderTitle()} buttons={isMobile ? [] : buttons} />
			{showLogs && (
				<MessageEventModal
					path={`/${organization.id}/logs/message_events`}
					resourceId={Number(paymentMethod.id)}
					resourceType={"PAYMENT_METHOD"}
					onHide={() => setShowLogs(!showLogs)}
					organizationId={organization.id}
				/>
			)}
			<PaymentMethodForm
				paymentMethod={paymentMethod}
				tPayPayments={tPayPayments}
				setTPayPayments={setTPayPayments}
				fetchPaymentMethod={onFetchPaymentMethod}
			/>
		</>
	);
};

export default OrganizationSettingsPaymentMethodsEditPage;
